import { useCallback, useEffect, useRef, useState } from 'react';

export type GuidedTimerStatus = 'idle' | 'running' | 'paused' | 'done';

export interface GuidedTimer {
  status: GuidedTimerStatus;
  /** Whole seconds left (0 when done). */
  remainingSec: number;
  /** Length of the current run, including adjustments. */
  totalSec: number;
  start: (seconds: number) => void;
  pause: () => void;
  resume: () => void;
  /** ±seconds while running or paused (never below 1 s left). */
  adjust: (deltaSec: number) => void;
  /** Ends the wait now ("Skip"), without the done signal. */
  skip: () => void;
  reset: () => void;
}

const TICK_MS = 250;

/**
 * Countdown for test-kit waiting times (docs/voiceFeature.md, UC-16). It counts
 * against an end time (`endsAt`), never by adding up ticks: browsers throttle
 * timers in background tabs, so after returning to the tab the remaining time
 * is recomputed and still correct. `onDone` fires once when the time is up.
 */
export function useGuidedTimer(onDone: () => void, now: () => number = Date.now): GuidedTimer {
  const [status, setStatus] = useState<GuidedTimerStatus>('idle');
  const [remainingMs, setRemainingMs] = useState(0);
  const [totalSec, setTotalSec] = useState(0);
  const endsAt = useRef<number | null>(null);
  const pausedLeft = useRef(0);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const tick = useCallback(() => {
    if (endsAt.current === null) return;
    const left = endsAt.current - now();
    if (left <= 0) {
      endsAt.current = null;
      setRemainingMs(0);
      setStatus('done');
      onDoneRef.current();
      return;
    }
    setRemainingMs(left);
  }, [now]);

  useEffect(() => {
    if (status !== 'running') return undefined;
    const id = setInterval(tick, TICK_MS);
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [status, tick]);

  const start = useCallback(
    (seconds: number) => {
      endsAt.current = now() + seconds * 1000;
      setTotalSec(seconds);
      setRemainingMs(seconds * 1000);
      setStatus('running');
    },
    [now],
  );

  const pause = useCallback(() => {
    if (endsAt.current === null) return;
    pausedLeft.current = Math.max(0, endsAt.current - now());
    endsAt.current = null;
    setRemainingMs(pausedLeft.current);
    setStatus('paused');
  }, [now]);

  const resume = useCallback(() => {
    endsAt.current = now() + pausedLeft.current;
    setStatus('running');
  }, [now]);

  const adjust = useCallback(
    (deltaSec: number) => {
      const deltaMs = deltaSec * 1000;
      if (endsAt.current !== null) {
        endsAt.current = Math.max(now() + 1000, endsAt.current + deltaMs);
        setRemainingMs(endsAt.current - now());
      } else if (status === 'paused') {
        pausedLeft.current = Math.max(1000, pausedLeft.current + deltaMs);
        setRemainingMs(pausedLeft.current);
      } else {
        return;
      }
      setTotalSec((total) => Math.max(1, total + deltaSec));
    },
    [now, status],
  );

  const skip = useCallback(() => {
    endsAt.current = null;
    setRemainingMs(0);
    setStatus('done');
  }, []);

  const reset = useCallback(() => {
    endsAt.current = null;
    setRemainingMs(0);
    setTotalSec(0);
    setStatus('idle');
  }, []);

  return { status, remainingSec: Math.ceil(remainingMs / 1000), totalSec, start, pause, resume, adjust, skip, reset };
}
